/**
 * FeatureFlagContext - A React context provider for managing feature flags using Unleash
 *
 * This component provides feature flag functionality throughout the application by:
 * 1. Setting up the Unleash client with configuration from environment variables
 * 2. Managing user context for feature flag evaluation
 * 3. Automatically updating the Unleash context when user authentication changes
 *
 * The context is configured with:
 * - Unleash proxy URL and client key from environment variables
 * - 15-second refresh interval for flag updates
 * - Application name and environment settings
 * - User context for personalized feature flag evaluation
 *
 * Usage:
 * Wrap your application with this provider to enable feature flag functionality:
 * <FlagProviderWrapper>
 *   <YourApp />
 * </FlagProviderWrapper>
 */

import React, { useEffect } from 'react';

import { createSelector } from '@reduxjs/toolkit';
import { FlagProvider, IConfig, useUnleashContext } from '@unleash/proxy-client-react';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/user/userSlice';

const flagSelector = createSelector(selectUser, (user) => ({
  userId: user?._id ?? null
}));

const InnerFlagWrapper = ({ children }: { children: React.ReactNode }) => {
  const { userId } = useSelector(flagSelector);
  const updateContext = useUnleashContext();

  // Update context after login
  useEffect(() => {
    if (userId) {
      updateContext({ userId });
    }
  }, [userId]);

  return <>{children}</>;
};

const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId } = useSelector(flagSelector);

  const config: IConfig = {
    appName: 'LifeMD Physician Portal',
    clientKey: import.meta.env.VITE_UNLEASH_CLIENT_KEY,
    context: {
      userId
    },
    environment: import.meta.env.VITE_UNLEASH_ENV,
    refreshInterval: 15,
    url: import.meta.env.VITE_UNLEASH_PROXY_URL
  };

  return (
    <FlagProvider config={config}>
      <InnerFlagWrapper>{children}</InnerFlagWrapper>
    </FlagProvider>
  );
};

export default FeatureFlagProvider;
